import clsx from 'clsx';
import { has } from 'lodash';
import { AssetLeafChildrenSelectFormComponent } from '@/formbuilder/AssetLeafChildrenSelectFormComponent.atom';
import { ButtonFormComponent } from '@/formbuilder/ButtonFormComponent';
import { ButtonGroupFormComponent } from '@/formbuilder/ButtonGroupFormComponent';
import { ButtonWithPopoverFormComponent } from '@/formbuilder/ButtonWithPopoverFormComponent';
import { CKEditorFormComponent } from '@/formbuilder/CKEditorFormComponent';
import { CapsuleGroupFormComponent } from '@/formbuilder/CapsuleGroupFormComponent';
import { CapsuleInputFormComponent } from '@/formbuilder/CapsuleInputFormComponent.molecule';
import { CheckboxFormComponent } from '@/formbuilder/CheckboxFormComponent.atom';
import { CheckboxTableFormComponent } from '@/formbuilder/CheckboxTableFormComponent.atom';
import { ClickableLinkFormComponent } from '@/formbuilder/ClickableLinkFormComponent.atom';
import { ColorPickerFormComponent } from '@/formbuilder/ColorPickerFormComponent.molecule';
import { CompositesSelectorFormComponent } from '@/formbuilder/CompositeLogicSelectorFormComponent.molecule';
import { CopyableInputFormComponent } from '@/formbuilder/CopyableInputFormComponent.molecule';
import { DatasourceSelectFormComponent } from '@/formbuilder/DatasourceSelectFormComponent.atom';
import { DatePickerFormComponent } from '@/formbuilder/DatePickerFormComponent.atom';
import { DateTimeEntryFormComponent } from '@/formbuilder/DateTimeEntryFormComponent';
import { DateTimeRangeFormComponent } from '@/formbuilder/DateTimeRangeFormComponent';
import { DescriptionFormComponent } from '@/formbuilder/DescriptionFormComponent.atom';
import { DraggableCollapsibleFormBlock } from '@/formbuilder/DraggableCollapsibleFormBlock';
import { DurationFormComponent } from '@/formbuilder/DurationFormComponent';
import { ErrorMessageFormComponent } from '@/formbuilder/ErrorMessageFormComponent.atom';
import { FileDropperFormComponent } from '@/formbuilder/FileDropperFormComponent.atom';
import { FormulaEditorFormComponent } from '@/formbuilder/FormulaEditorFormComponent';
import { FormulaItemTableFormComponent } from '@/formbuilder/FormulaItemTableFormComponent.atom';
import { FormulaVariablesFormComponent } from '@/formbuilder/FormulaVariablesFormComponent';
import { HiddenFormComponent } from '@/formbuilder/HiddenFormComponent.atom';
import { IconFormComponent } from '@/formbuilder/IconFormComponent.atom';
import { IconSelectFormComponent } from '@/formbuilder/IconSelectFormComponent.atom';
import { ImageSelectFormComponent } from '@/formbuilder/ImageSelectFormComponent';
import { InputTableFormComponent } from '@/formbuilder/InputTableFormComponent.molecule';
import { InputWithLinkedButtonsFormComponent } from '@/formbuilder/InputWithLinkedButtonsFormComponent';
import { ItemSelectAndSearchFormComponent } from '@/formbuilder/ItemSelectAndSearchFormComponent.atom';
import { ItemSelectFormComponent } from '@/formbuilder/ItemSelectFormComponent.atom';
import { JsonTextAreaFormComponent } from '@/formbuilder/JsonTextAreaFormComponent.atom';
import { LabelFormComponent } from '@/formbuilder/LabelFormComponent.atom';
import { LocationLookupFormComponent } from '@/formbuilder/LocationLookupFormComponent.atom';
import { LowerUpperLimitsFormComponent } from '@/formbuilder/LowerUpperLimitsFormComponent';
import { MaxCapsuleDurationFormComponent } from '@/formbuilder/MaxCapsuleDurationFormComponent';
import { OperatorSelectFormComponent } from '@/formbuilder/OperatorSelectFormComponent.atom';
import { PasswordCheckFormComponent } from '@/formbuilder/PasswordCheckFormComponent.atom';
import { PasswordGroupFormComponent } from '@/formbuilder/PasswordGroupFormComponent';
import { PatternFormComponent } from '@/formbuilder/PatternFormComponent';
import { PriorityPickerFormComponent } from '@/formbuilder/PriorityPickerFormComponent.molecule';
import { RadioButtonGroupFormComponent } from '@/formbuilder/RadioButtonGroupFormComponent.atom';
import { ScatterPlotRegionFormComponent } from '@/formbuilder/ScatterPlotRegionFormComponent';
import { ScheduleFormComponent } from '@/formbuilder/ScheduleFormComponent';
import { SearchTitleFormComponent } from '@/formbuilder/SearchTitleFormComponent';
import { SearchWidgetFormComponent } from '@/formbuilder/SearchWidgetFormComponent';
import { SelectColumnFromTableFormComponent } from '@/formbuilder/SelectColumnFromTableFormComponent';
import { SelectFolderFormComponent } from '@/formbuilder/SelectFolderFormComponent';
import { SelectFormComponent } from '@/formbuilder/SelectFormComponent';
import { SelectIdentityFormComponent } from '@/formbuilder/SelectIdentityFormComponent.atom';
import { SelectItemSearchFormComponent } from '@/formbuilder/SelectItemSearchFormComponent';
import { SelectUnitFormComponent } from '@/formbuilder/SelectUnitFormComponent.atom';
import { SelectWorkbookFormComponent } from '@/formbuilder/SelectWorkbookFormComponent';
import { StatisticSelectorFormComponent } from '@/formbuilder/StatisticSelectorFormComponent.atom';
import { TextFieldFormComponent } from '@/formbuilder/TextFieldFormComponent.atom';
import { TimeZoneSelectorFormComponent } from '@/formbuilder/TimeZoneSelectorFormComponent.atom';
import { ToggleFormComponent } from '@/formbuilder/ToggleFormComponent.molecule';
import { ToggleInputSelectItemSearchFormComponent } from '@/formbuilder/ToggleInputSelectItemSearchFormComponent';
import { ValueBinsFormComponent } from '@/formbuilder/ValueBinsFormComponent';
import { ValueWithSymbolFormComponent } from '@/formbuilder/ValueWithSymbolFormComponent';
import { ValueWithUnitsFormComponent } from '@/formbuilder/ValueWithUnitsFormComponent';
import {
  AdvancedFormBlock,
  CollapsibleFormBlock,
  ConditionalFormBlock,
  DisplayOnlyFormElementWrapper,
  FormBlock,
  FormGroupBlock,
  FormGroupWithTitleBlock,
  FormRowBlock,
  RemovableFormBlock,
  RemovableFormRowBlock,
  TitledFormRowBlock,
} from '@/formbuilder/formBuildingBlocks';
import { FlexLayoutSplitter } from '@/formbuilder/formElements/FlexLayoutSplitter';
import { OperationalizeOutputSelectorFormComponent } from '@/tools/operationalizeStatisticsSummary/OperationalizeOutputSelectorFormComponent.molecule';
import type React from 'react';
import type {
  AdvancedFormGroup,
  CollapsibleFormGroup,
  DraggableCollapsibleFormGroup,
  FormElement,
  FormGroup,
  FormGroupWithTitle,
  FormRow,
  RemovableFormGroup,
  RemovableFormRow,
} from '@/formbuilder/formBuilder.constants';

export function renderIt(def: FormElement) {
  if (!def.component) {
    throw new Error('Every FormElement must provide a valid component or it can not be rendered.');
  }
  switch (def.component) {
    // Structural components with children
    case 'FormGroup':
      return renderFormGroup(def);
    case 'FormGroupWithTitle':
      return renderFormGroupWithTitle(def);
    case 'FormRow':
      return renderFormRow(def);
    case 'RemovableFormRow':
      return renderRemovableFormRow(def);
    case 'AdvancedFormGroup':
      return renderAdvancedFormGroup(def);
    case 'RemovableFormGroup':
      return renderRemovableFormGroup(def);
    case 'CollapsibleFormGroup':
      return renderCollapsibleFormGroup(def);
    case 'DraggableCollapsibleFormBlock':
      return renderDraggableCollapsibleFormBlock(def);
    // Leaf components
    case 'CheckboxFormComponent':
      return renderWrapped(CheckboxFormComponent, def);
    case 'ErrorMessageFormComponent':
      return renderWrapped(ErrorMessageFormComponent, def);
    case 'TextFieldFormComponent':
      return renderWrapped(TextFieldFormComponent, def);
    case 'JsonTextAreaFormComponent':
      return renderWrapped(JsonTextAreaFormComponent, def);
    case 'IconSelectFormComponent':
      return renderWrapped(IconSelectFormComponent, def);
    case 'ItemSelectFormComponent':
      return renderWrapped(ItemSelectFormComponent, def);
    case 'LabelFormComponent':
      return renderWrapped(LabelFormComponent, def);
    case 'HiddenFormComponent':
      return renderWrapped(HiddenFormComponent, def);
    case 'OperatorSelectFormComponent':
      return renderWrapped(OperatorSelectFormComponent, def);
    case 'RadioButtonGroupFormComponent':
      return renderWrapped(RadioButtonGroupFormComponent, def);
    case 'SearchTitleFormComponent':
      return renderWrapped(SearchTitleFormComponent, def);
    case 'ValueWithUnitsFormComponent':
      return renderWrapped(ValueWithUnitsFormComponent, def);
    case 'TimeZoneSelectorFormComponent':
      return renderWrapped(TimeZoneSelectorFormComponent, def);
    case 'ClickableLinkFormComponent':
      return renderWrapped(ClickableLinkFormComponent, def);
    case 'CheckboxTableFormComponent':
      return renderWrapped(CheckboxTableFormComponent, def);
    case 'SelectUnitFormComponent':
      return renderWrapped(SelectUnitFormComponent, def);
    case 'FileDropperFormComponent':
      return renderWrapped(FileDropperFormComponent, def);
    case 'CompositesSelectorFormComponent':
      return renderWrapped(CompositesSelectorFormComponent, def);
    case 'MaxCapsuleDurationFormComponent':
      return renderWrapped(MaxCapsuleDurationFormComponent, def);
    case 'IconFormComponent':
      return renderWrapped(IconFormComponent, def);
    case 'CapsuleInputFormComponent':
      return renderWrapped(CapsuleInputFormComponent, def);
    case 'InputTableFormComponent':
      return renderWrapped(InputTableFormComponent, def);
    case 'StatisticSelectorFormComponent':
      return renderWrapped(StatisticSelectorFormComponent, def);
    case 'ColorPickerFormComponent':
      return renderWrapped(ColorPickerFormComponent, def);
    case 'PriorityPickerFormComponent':
      return renderWrapped(PriorityPickerFormComponent, def);
    case 'ValueWithSymbolFormComponent':
      return renderWrapped(ValueWithSymbolFormComponent, def);
    case 'PatternFormComponent':
      return renderWrapped(PatternFormComponent, def);
    case 'SelectFormComponent':
      return renderWrapped(SelectFormComponent, def);
    case 'ButtonFormComponent':
      return renderWrapped(ButtonFormComponent, def);
    case 'ButtonGroupFormComponent':
      return renderWrapped(ButtonGroupFormComponent, def);
    case 'DisplayOnlyFormElementWrapper':
      return renderWrapped(DisplayOnlyFormElementWrapper, def);
    case 'ImageSelectFormComponent':
      return renderWrapped(ImageSelectFormComponent, def);
    case 'PasswordCheckFormComponent':
      return renderWrapped(PasswordCheckFormComponent, def);
    case 'PasswordGroupFormComponent':
      return renderWrapped(PasswordGroupFormComponent, def);
    case 'FormulaVariablesFormComponent':
      return renderWrapped(FormulaVariablesFormComponent, def);
    case 'FormulaEditorFormComponent':
      return renderWrapped(FormulaEditorFormComponent, def);
    case 'CapsuleGroupFormComponent':
      return renderWrapped(CapsuleGroupFormComponent, def);
    case 'SearchWidgetFormComponent':
      return renderWrapped(SearchWidgetFormComponent, def);
    case 'SelectIdentityFormComponent':
      return renderWrapped(SelectIdentityFormComponent, def);
    case 'ScatterPlotRegionFormComponent':
      return renderWrapped(ScatterPlotRegionFormComponent, def);
    case 'DateTimeRangeFormComponent':
      return renderWrapped(DateTimeRangeFormComponent, def);
    case 'DateTimeEntryFormComponent':
      return renderWrapped(DateTimeEntryFormComponent, def);
    case 'ValueBinsFormComponent':
      return renderWrapped(ValueBinsFormComponent, def);
    case 'ButtonWithPopoverFormComponent':
      return renderWrapped(ButtonWithPopoverFormComponent, def);
    case 'SelectItemSearchFormComponent':
      return renderWrapped(SelectItemSearchFormComponent, def);
    case 'SelectColumnFromTableFormComponent':
      return renderWrapped(SelectColumnFromTableFormComponent, def);
    case 'SelectFolderFormComponent':
      return renderWrapped(SelectFolderFormComponent, def);
    case 'SelectWorkbookFormComponent':
      return renderWrapped(SelectWorkbookFormComponent, def);
    case 'CopyableInputFormComponent':
      return renderWrapped(CopyableInputFormComponent, def);
    case 'ScheduleFormComponent':
      return renderWrapped(ScheduleFormComponent, def);
    case 'DurationFormComponent':
      return renderWrapped(DurationFormComponent, def);
    case 'InputWithLinkedButtonsFormComponent':
      return renderWrapped(InputWithLinkedButtonsFormComponent, def);
    case 'DatePickerFormComponent':
      return renderWrapped(DatePickerFormComponent, def);
    case 'CKEditorFormComponent':
      return renderWrapped(CKEditorFormComponent, def);
    case 'DatasourceSelectFormComponent':
      return renderWrapped(DatasourceSelectFormComponent, def);
    case 'ToggleFormComponent':
      return renderWrapped(ToggleFormComponent, def);
    case 'ToggleInputSelectItemSearchFormComponent':
      return renderWrapped(ToggleInputSelectItemSearchFormComponent, def);
    case 'LocationLookupFormComponent':
      return renderWrapped(LocationLookupFormComponent, def);
    case 'DescriptionFormComponent':
      return renderWrapped(DescriptionFormComponent, def);
    case 'FormulaItemTableFormComponent':
      return renderWrapped(FormulaItemTableFormComponent, def);
    case 'LowerUpperLimitsFormComponent':
      return renderWrapped(LowerUpperLimitsFormComponent, def);
    case 'ItemSelectAndSearchFormComponent':
      return renderWrapped(ItemSelectAndSearchFormComponent, def);
    case 'FlexLayoutSplitter':
      return renderWrapped(FlexLayoutSplitter, def);
    case 'OperationalizeOutputSelectorFormComponent':
      return renderWrapped(OperationalizeOutputSelectorFormComponent, def);
    case 'AssetLeafChildrenSelectFormComponent':
      return renderWrapped(AssetLeafChildrenSelectFormComponent, def);
    default: {
      const exhaustiveCheck: never = def;
      throw new Error(
        `Please supply an existing FormComponent or add ${
          (exhaustiveCheck as any).component
        } as a supported component.`,
      );
    }
  }
}

function renderFormGroup(def: FormGroup) {
  const {
    key,
    name,
    components,
    testId,
    formGroupExtraClassNames,
    showBracket,
    showScroll = false,
    showGroupStyles,
  } = def;
  const children = components.map(renderIt);
  const content = (
    <FormGroupBlock
      key={key}
      name={name}
      testId={testId}
      showGroupStyles={showGroupStyles}
      formGroupExtraClassNames={formGroupExtraClassNames}
      showBracket={showBracket}
      showScroll={showScroll}>
      {children}
    </FormGroupBlock>
  );

  return finalizeForRender(def, content);
}

function renderFormGroupWithTitle(def: FormGroupWithTitle) {
  const {
    key,
    name,
    components,
    testId,
    extraClassNames,
    showBracket,
    showScroll = false,
    showTitle = false,
    title,
    titleLink,
    titleLinkAction,
    additionalTitleLink,
    additionalTitleLinkAction,
  } = def;
  const children = components.map(renderIt);
  const content = (
    <FormGroupWithTitleBlock
      key={key}
      name={name}
      testId={testId}
      extraClassNames={extraClassNames}
      showBracket={showBracket}
      showTitle={showTitle}
      title={title}
      titleLink={titleLink}
      titleLinkAction={titleLinkAction}
      additionalTitleLink={additionalTitleLink}
      additionalTitleLinkAction={additionalTitleLinkAction}
      showScroll={showScroll}>
      {children}
    </FormGroupWithTitleBlock>
  );

  return finalizeForRender(def, content);
}

function renderAdvancedFormGroup(def: AdvancedFormGroup) {
  const {
    key,
    name,
    components,
    testId,
    toolName,
    toolId,
    alternateName,
    extraClassNames,
    iconStyle,
    collapsed,
    onToggle,
  } = def;
  const children = components.map(renderIt);
  const content = (
    <AdvancedFormBlock
      key={key || name}
      name={name}
      testId={testId}
      toolName={toolName}
      toolId={toolId}
      alternateName={alternateName}
      extraClassNames={extraClassNames}
      iconStyle={iconStyle}
      collapsed={collapsed}
      onToggle={onToggle}>
      {children}
    </AdvancedFormBlock>
  );
  return finalizeForRender(def, content);
}

function renderCollapsibleFormGroup(def: CollapsibleFormGroup) {
  const {
    components,
    testId,
    deleteIconAction,
    hideDeleteIcon = false,
    additionalIcon,
    additionalIconAction,
    additionalIconAriaLabel,
    additionalIconAsButton,
    additionalIconTestId,
    additionalIconTooltip,
    extraClassNames,
    xstyle,
    iconXstyle,
    contentXstyle,
    labelXstyle,

    labelExtraClassNames,
    isCollapsed = false,
    toggleCollapse,
    label,
    isLast = false,
    isNewlyAdded = false,
    newLabel,
  } = def;
  const children = components.map(renderIt);
  const content = (
    <CollapsibleFormBlock
      xstyle={xstyle}
      iconXstyle={iconXstyle}
      contentXstyle={contentXstyle}
      labelXstyle={labelXstyle}
      hideDeleteIcon={hideDeleteIcon}
      testId={testId}
      deleteIconAction={deleteIconAction}
      additionalIcon={additionalIcon}
      additionalIconAction={additionalIconAction}
      additionalIconAriaLabel={additionalIconAriaLabel}
      additionalIconAsButton={additionalIconAsButton}
      additionalIconTestId={additionalIconTestId}
      additionalIconTooltip={additionalIconTooltip}
      isCollapsed={isCollapsed}
      toggleCollapse={toggleCollapse}
      extraClassNames={extraClassNames}
      labelExtraClassNames={labelExtraClassNames}
      label={label}
      isLast={isLast}
      isNewlyAdded={isNewlyAdded}
      newLabel={newLabel}>
      {children}
    </CollapsibleFormBlock>
  );
  return finalizeForRender(def, content);
}

function renderDraggableCollapsibleFormBlock(def: DraggableCollapsibleFormGroup) {
  return renderWrapped(DraggableCollapsibleFormBlock, def);
}

function renderRemovableFormGroup(def: RemovableFormGroup) {
  const {
    components,
    testId,
    iconAction,
    hideIcon = false,
    extraClassNames,
    xstyle,
    iconXstyle,

    iconHasMargin = false,
    iconTooltip,
    showLabelAfterRows,
    labelAfterRows,
    showAddBlockButton,
    addBlockButtonAction,
    addBlockButtonTooltip,
  } = def;
  const children = components.map(renderIt);
  const content = (
    <RemovableFormBlock
      xstyle={xstyle}
      iconXstyle={iconXstyle}
      hideIcon={hideIcon}
      testId={testId}
      iconAction={iconAction}
      iconHasMargin={iconHasMargin}
      iconTooltip={iconTooltip}
      showLabelAfterRows={showLabelAfterRows}
      labelAfterRows={labelAfterRows}
      showAddBlockButton={showAddBlockButton}
      addBlockButtonAction={addBlockButtonAction}
      addBlockButtonTooltip={addBlockButtonTooltip}
      extraClassNames={extraClassNames}>
      {children}
    </RemovableFormBlock>
  );
  return finalizeForRender(def, content);
}

function renderFormRow(def: FormRow) {
  const { name, components, testId, extraClassNames, title, helpText, wrapperClassNames } = def;
  const children = components.map(renderIt);
  const content = title ? (
    <TitledFormRowBlock
      name={name}
      testId={testId}
      extraClassNames={clsx('formRow', extraClassNames)}
      title={title}
      helpText={helpText}
      wrapperClassNames={wrapperClassNames}>
      {children}
    </TitledFormRowBlock>
  ) : (
    <FormRowBlock name={name} testId={testId} extraClassNames={clsx('formRow', extraClassNames)}>
      {children}
    </FormRowBlock>
  );

  return finalizeForRender(def, content);
}

function renderRemovableFormRow(def: RemovableFormRow) {
  const { name, components, testId, removeAction, hideRemoveIcon = false, extraClassNames, wrapperClassNames } = def;
  const children = components.map(renderIt);
  const content = (
    <RemovableFormRowBlock
      hideRemoveIcon={hideRemoveIcon}
      name={name}
      testId={testId}
      removeAction={removeAction}
      wrapperClassNames={wrapperClassNames}
      extraClassNames={clsx('formRow', extraClassNames)}>
      {children}
    </RemovableFormRowBlock>
  );
  return finalizeForRender(def, content);
}

function renderWrapped(What: React.ComponentType<any>, props: FormElement) {
  const { name } = props;
  return finalizeForRender(props, <What {...props} key={name} />);
}

function finalizeForRender(def: FormElement, content: React.JSX.Element) {
  const { number, includeIf, name, key, formBlockClassNames } = def;
  const finalContent: React.JSX.Element = number ? (
    <FormBlock name={name} formBlockClassNames={formBlockClassNames}>
      {content}
    </FormBlock>
  ) : (
    content
  );

  return (
    <ConditionalFormBlock key={key || name} isConditional={has(def, 'includeIf')} show={includeIf} name={name}>
      {finalContent}
    </ConditionalFormBlock>
  );
}
